import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {mapParseItem, mapParseList} from '@brunata-angular/util';
import {SelectableItem} from '@brunata-angular/ui';

import {BehaviorSubject, map, Observable} from 'rxjs';
import {environment} from 'src/environments/environment';
import {z} from 'zod';
import {
    EventAlarm,
    EventAlarmCreateRequest,
    eventAlarmSchema,
    EventAlarmTemplate,
    eventAlarmTemplateSchema,
    EventAlarmUpdateRequest,
    EventAlarmWithTranslations,
    vendorModel,
    VendorModel,
} from './event-alarm.model';
import {SortingType} from '@brunata-angular/ui';

export type FilterData = {
    meterModelCriteriaMismatch: SelectableItem[] | null;
    eventTypeId: SelectableItem[] | null;
    type: SelectableItem[] | null;
    alarmName: string | null;
};

@Injectable({
    providedIn: 'root',
})
export class EventAlarmsService {
    sorting = new BehaviorSubject<SortingType<EventAlarmWithTranslations>>({
        column: 'alarmName',
        direction: 'asc',
    });

    page = new BehaviorSubject<number>(1);
    pageSize = new BehaviorSubject<number>(20);

    filterSubject: BehaviorSubject<FilterData> = new BehaviorSubject<FilterData>({
        meterModelCriteriaMismatch: [],
        eventTypeId: [],
        type: [],
        alarmName: '',
    });

    baseUrl = `${environment.onlineServiceUrl}/buildings`;

    constructor(private http: HttpClient) {}

    setFilterColumns(filterFormValues: FilterData): void {
        this.filterSubject.next(filterFormValues);
    }

    updateSorting(sorting: SortingType<EventAlarmWithTranslations>): void {
        this.sorting.next(sorting);
    }

    getEventAlarms(buildingNo: string): Observable<EventAlarm[]> {
        return this.http
            .get(`${this.baseUrl}/${buildingNo}/alarms`, {
                withCredentials: true,
            })
            .pipe(
                mapParseItem(
                    z
                        .object({
                            eventAlarms: z.array(eventAlarmSchema),
                        })
                        .transform(data => data.eventAlarms),
                ),
            );
    }

    getEventAlarm(buildingNo: string, subscriptionId: string): Observable<EventAlarm> {
        return this.http
            .get(`${this.baseUrl}/${buildingNo}/alarms/eventbased/${subscriptionId}`, {
                withCredentials: true,
            })
            .pipe(mapParseItem(eventAlarmSchema));
    }

    createEventAlarm(buildingNo: string, eventAlarm: EventAlarmCreateRequest): Observable<string> {
        return this.http
            .post(`${this.baseUrl}/${buildingNo}/alarms/eventbased`, eventAlarm, {
                withCredentials: true,
            })
            .pipe(map(id => id.toString()));
    }

    updateEventAlarm(
        buildingNo: string,
        subscriptionId: string,
        eventAlarm: EventAlarmUpdateRequest,
    ): Observable<unknown> {
        const url = `${this.baseUrl}/${buildingNo}/alarms/eventbased/${subscriptionId}`;

        return this.http.put(url, eventAlarm, {withCredentials: true});
    }

    getEventTypesForBuilding(buildingNo: string): Observable<EventAlarmTemplate[]> {
        return this.http
            .get(`${this.baseUrl}/${buildingNo}/eventtypes`, {
                withCredentials: true,
            })
            .pipe(mapParseList(eventAlarmTemplateSchema));
    }

    getVendorModelsForEventType(
        buildingNo: string,
        eventTypeId: string,
    ): Observable<VendorModel[]> {
        return this.http
            .get(
                `${this.baseUrl}/${buildingNo}/vendormodelsforeventtype?eventtypeid=${eventTypeId}`,
                {
                    withCredentials: true,
                },
            )
            .pipe(mapParseList(vendorModel));
    }

    deleteEventAlarm(buildingNo: string, subscriptionId: string): Observable<unknown> {
        return this.http.delete(
            `${this.baseUrl}/${buildingNo}/alarms/eventbased/${subscriptionId}`,
            {withCredentials: true},
        );
    }
}
